import { CdkDrag, CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LoadingBoxComponent } from '../../../../../shared/components/loading-box/loading-box.component';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';
import { TipoEtapaKanbanEnum } from '../../../../../shared/enums/vendas/tipo-etapa-kanban.enum';
import { VendaDocumentoStruct } from '../../../../../shared/services/structs/vendas/venda-documento.struct';
import { ChaveEtapaKanbanEnum } from '../../../../../shared/enums/vendas/chave-etapa-kanban.enum';
import { VendaTipoDocumentoEnum } from '../../../../../shared/enums/vendas/venda-tipo-documento.enum';
import {
    PvColunaVm,
    PvMovimentoCard,
    PvPermissoesOportunidade,
    resolverPermissoesOportunidade,
} from '../../processo-venda.models';
import { PvCardComponent } from '../pv-card/pv-card.component';

/**
 * Uma coluna do board: cabeçalho com totais e a pilha de cards.
 *
 * O arraste está com a marcação montada mas DESABILITADO nesta rodada — a validação de
 * movimentação (destino habilitado, perfil autorizado, motivo obrigatório, teto de desconto)
 * é da fase de mecânicas, e liberar o arraste antes dela deixaria o usuário mover cards sem
 * nenhuma regra aplicada.
 */
@Component({
    selector: 'app-pv-coluna',
    standalone: true,
    imports: [
        CommonModule,
        MatIconModule,
        MatTooltipModule,
        LoadingBoxComponent,
        CdkDropList,
        CdkDrag,
        PvCardComponent,
        AbntPipe,
    ],
    templateUrl: './pv-coluna.component.html',
    styleUrls: ['./pv-coluna.component.css'],
})
export class PvColunaComponent {
    @Input({ required: true }) coluna: PvColunaVm;

    /** Toggle "Mostrar soma por coluna" de Configurações >> Vendas >> Kanban. */
    @Input() isMostrarSoma = true;

    /** Coluna de faixa de rotina externa: o card não abre o documento e não aceita arraste. */
    @Input() isSomenteLeitura = false;

    /** Renderiza a coluna fantasma do carregamento, com a mesma caixa da coluna real. */
    @Input() isSkeleton = false;

    /** Quantos cards fantasma desenhar — mesma quantidade do processo-compras. */
    public readonly slotsEsqueleto = [1, 2, 3];

    @Output() recolher = new EventEmitter<PvColunaVm>();
    @Output() verMais = new EventEmitter<PvColunaVm>();
    @Output() abrirCard = new EventEmitter<VendaDocumentoStruct>();

    /** Card solto nesta coluna, já aprovado pelo enterPredicate. */
    @Output() moverCard = new EventEmitter<PvMovimentoCard>();

    /**
     * Decide se ESTA coluna aceita o card arrastado. Vem do shell porque a resposta depende da
     * coluna de origem, que a coluna de destino não conhece — só o board tem as duas pontas.
     */
    @Input() aceitaCard: (card: VendaDocumentoStruct, destino: PvColunaVm) => boolean = () => false;

    /** Sem permissão de alterar, o card não sai do lugar. */
    @Input() podeArrastar = false;

    /** Sem permissão de inserir, "Criar modelo" fica desabilitado no menu do card. */
    @Input() podeInserir = false;

    /** Sem permissão de excluir, "Excluir" fica desabilitado no menu do card. */
    @Input() podeExcluir = false;
    @Input() podeLancarFinanceiro = false;
    @Input() podeExcluirFinanceiro = false;
    @Input() podeLancarEstoque = false;
    @Input() podeExcluirEstoque = false;

    @Output() criarModelo = new EventEmitter<VendaDocumentoStruct>();
    @Output() clonar = new EventEmitter<VendaDocumentoStruct>();
    @Output() verHistorico = new EventEmitter<VendaDocumentoStruct>();
    @Output() verOportunidadesCliente = new EventEmitter<VendaDocumentoStruct>();
    @Output() excluir = new EventEmitter<VendaDocumentoStruct>();
    @Output() verOportunidade = new EventEmitter<VendaDocumentoStruct>();
    @Output() criarOportunidade = new EventEmitter<VendaDocumentoStruct>();
    @Output() vincularOportunidade = new EventEmitter<VendaDocumentoStruct>();
    @Output() desvincularOportunidade = new EventEmitter<VendaDocumentoStruct>();

    /**
     * Repassado ao card sem interpretação: as permissões de oportunidade são do shell. Opcional só por
     * causa da coluna fantasma do carregamento, que não tem cards.
     */
    @Input() permissoesOportunidade: PvPermissoesOportunidade = resolverPermissoesOportunidade([]);
    @Output() lancarFinanceiro = new EventEmitter<VendaDocumentoStruct>();
    @Output() excluirFinanceiro = new EventEmitter<VendaDocumentoStruct>();
    @Output() lancarEstoque = new EventEmitter<VendaDocumentoStruct>();
    @Output() excluirEstoque = new EventEmitter<VendaDocumentoStruct>();
    /** Clique no indicador F: os lançamentos do financeiro, só para consultar. */
    @Output() verFinanceiro = new EventEmitter<VendaDocumentoStruct>();

    /**
     * Card em voo neste instante, ou null. Vem do shell para cada coluna poder se pintar como
     * destino válido ou bloqueado ENQUANTO o usuário arrasta — antes era preciso soltar para
     * descobrir, e a recusa acontecia em silêncio.
     */
    @Input() cardEmArraste: VendaDocumentoStruct | null = null;

    @Output() arrasteIniciou = new EventEmitter<VendaDocumentoStruct>();
    @Output() arrasteTerminou = new EventEmitter<void>();

    /**
     * Ponteiro entrou nesta coluna. Serve para o board saber sobre QUAL coluna o cursor está
     * durante o arraste — o CDK não avisa quando o ponteiro passa por uma coluna que recusa,
     * então sem isso o fantasma fica parado na última coluna válida enquanto o cursor já está
     * em outra, dizendo ao usuário uma coisa e ao sistema outra.
     */
    @Output() ponteiroEntrou = new EventEmitter<PvColunaVm>();

    public readonly tipoEtapaKanbanEnum = TipoEtapaKanbanEnum;

    /**
     * Toque exige segurar antes de arrastar; mouse continua imediato.
     *
     * No celular o gesto de arrastar e o de rolar começam iguais — dedo na tela e move. Sem
     * atraso, tentar rolar o board levaria o card junto e mudaria a etapa do documento sem o
     * usuário querer. Com o atraso, o CDK cancela o arraste se o dedo se mover antes do prazo,
     * então a rolagem continua natural e só quem realmente segurou move o card.
     *
     * 250ms é o patamar usual de "toque longo" — curto o bastante para não parecer travado.
     */
    public readonly atrasoDeArraste = { touch: 250, mouse: 0 };

    /**
     * Cadeado no cabeçalho. São dois casos diferentes, e o tooltip distingue:
     * - Especial: etapa fixa do sistema — nome e exclusão travados na configuração. Ela CONTINUA
     *   sendo destino válido de arraste quando a movimentação estiver configurada (Faturado →
     *   Cancelado, por exemplo), então dizer que "não é destino" era mentira.
     * - Envio e retirada: aí sim ninguém arrasta — quem move é a rotina externa.
     */
    public get exibeCadeado(): boolean {
        return (
            this.coluna.idTipoEtapaKanban === TipoEtapaKanbanEnum.Especial ||
            this.coluna.idTipoEtapaKanban === TipoEtapaKanbanEnum.EnvioRetirada
        );
    }

    public get tooltipCadeado(): string {
        return this.coluna.idTipoEtapaKanban === TipoEtapaKanbanEnum.EnvioRetirada
            ? 'Coluna fixa do sistema — movimentada apenas pela rotina externa'
            : 'Etapa especial fixa do sistema';
    }

    /** Placeholder da coluna vazia, nos três textos que a story define. */
    public get textoVazio(): string {
        if (this.coluna.idTipoEtapaKanban === TipoEtapaKanbanEnum.EnvioRetirada) {
            return 'Nenhum pedido em trânsito';
        }

        // Modelo é catálogo, não etapa: "nenhum card" ali não diria nada ao usuário.
        if (this.coluna.chave === ChaveEtapaKanbanEnum.Modelo) {
            return 'Nenhum modelo salvo';
        }

        return 'Nenhum card nesta etapa';
    }

    /**
     * Predicado do CDK: chamado enquanto o card paira sobre esta coluna. Recusar aqui é melhor
     * que aceitar e reverter — a coluna simplesmente não abre espaço, e o usuário percebe que
     * o destino é inválido antes de soltar.
     *
     * Propriedade de seta, não método: o CDK chama a função sem contexto, e um método comum
     * perderia o `this`.
     */
    public readonly enterPredicate = (drag: CdkDrag<VendaDocumentoStruct>): boolean => {
        return this.aceitaCard(drag.data, this.coluna);
    };

    /** Destino possível para o card em voo: recebe realce. */
    public get isDestinoValido(): boolean {
        if (!this.cardEmArraste) return false;
        if (this.cardEmArraste.idEtapaKanban === this.coluna.idEtapaKanban) return false;

        return this.aceitaCard(this.cardEmArraste, this.coluna);
    }

    /**
     * Destino recusado para o card em voo: fica esmaecida.
     * A coluna de ORIGEM não entra aqui — ela não é um destino recusado, é de onde o card saiu.
     */
    public get isDestinoBloqueado(): boolean {
        if (!this.cardEmArraste) return false;
        if (this.cardEmArraste.idEtapaKanban === this.coluna.idEtapaKanban) return false;

        return !this.aceitaCard(this.cardEmArraste, this.coluna);
    }

    /**
     * "Criar modelo" só vale para orçamento em etapa do FLUXO PRINCIPAL — é a regra da story
     * ("Orçamento, fora de etapa especial"), e é o que impede um modelo de gerar outro: a
     * coluna Modelo é etapa especial.
     *
     * A mesma checagem existe no servidor; aqui ela só evita oferecer o que seria recusado.
     */
    public podeCriarModelo(card: VendaDocumentoStruct): boolean {
        if (!this.podeInserir) return false;
        if (card.idVendaTipoDocumento !== VendaTipoDocumentoEnum.Orcamento) return false;

        return this.coluna.idTipoEtapaKanban === TipoEtapaKanbanEnum.FluxoPrincipal;
    }

    public onDrop(evento: CdkDragDrop<VendaDocumentoStruct[]>): void {
        // Soltou na própria coluna: nada mudou de etapa, e reordenar dentro da coluna não é
        // uma operação que o board tenha hoje.
        if (evento.previousContainer === evento.container) return;

        // Soltou FORA da coluna que está com o fantasma.
        //
        // Acontece assim: o card passa por uma coluna que aceita (o fantasma entra nela) e o
        // usuário segue até uma que recusa. A coluna que recusa não recebe o fantasma, então ele
        // permanece na anterior — e o CDK considera o drop concluído ali, ainda que o ponteiro
        // esteja sobre outra. O card ia para uma coluna onde o usuário não soltou.
        //
        // isPointerOverContainer distingue os dois casos, e soltar fora vira desistência.
        if (!evento.isPointerOverContainer) return;

        this.moverCard.emit({ card: evento.item.data, destino: this.coluna });
    }
}
